import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  AiStatusDTO,
  AnaliseDaAdegaDTO,
  AnalyzeWindowsResultDTO,
  ConsultaDeRotuloDTO,
  ConviteCriadoDTO,
  CotaDeConsultasDTO,
  DestaquesDoConsumoDTO,
  PedidoDeConviteDTO,
  ConviteDTO,
  VagasDeFundadorDTO,
  AuthStatusDTO,
  BottleDTO,
  CellarDTO,
  CellarSummaryDTO,
  PasskeyDTO,
  ChangePasswordInput,
  Climatizacao,
  CreateBottleInput,
  DrinkFirstGroup,
  LabelReadingDTO,
  LoginInput,
  PairingResponseDTO,
  PapelDaAdega,
  PreferenciasDTO,
  RegisterInput,
  RetratoDaAdegaDTO,
  UpdateBottleInput,
  UploadedPhotoDTO,
} from '@adega/shared';
import { ApiError, ESPERA_DA_IA, ESPERA_DO_UPLOAD, api } from './client.js';

export const chaves = {
  auth: ['auth'] as const,
  cellars: ['cellars'] as const,
  cellar: (id: number | 'primeira') => ['cellar', id] as const,
  aiStatus: ['ai-status'] as const,
  convites: ['convites'] as const,
  // Debaixo de `convites` de propósito: criar, regenerar e cancelar convite já
  // invalidam `['convites']`, e o prefixo leva as vagas junto.
  vagasDeFundador: ['convites', 'vagas'] as const,
  pedidosDeConvite: ['pedidos-de-convite'] as const,
  cotaDeConsultas: ['cota-de-consultas'] as const,
  destaquesDoConsumo: ['destaques-do-consumo'] as const,
  bottles: (filtro?: string) => ['bottles', filtro ?? 'todas'] as const,
  bottle: (id: number) => ['bottle', id] as const,
  drinkFirst: ['drink-first'] as const,
  retrato: ['retrato'] as const,
  preferencias: ['preferencias'] as const,
  analiseDaAdega: ['analise-da-adega'] as const,
};

// ---------------------------------------------------------------- Sessão

export function useAuth() {
  return useQuery({
    queryKey: chaves.auth,
    queryFn: () => api.get<AuthStatusDTO>('/api/auth/me'),
    retry: false,
    staleTime: 60 * 1000,
  });
}

/** Login e cadastro invalidam tudo: os dados anteriores eram de outra sessão. */
function useAposAutenticar() {
  const client = useQueryClient();
  return (status: AuthStatusDTO) => {
    client.setQueryData(chaves.auth, status);
    void client.invalidateQueries();
  };
}

export function useLogin() {
  const aposAutenticar = useAposAutenticar();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<AuthStatusDTO>('/api/auth/login', input),
    onSuccess: aposAutenticar,
  });
}

export function useRegister() {
  const aposAutenticar = useAposAutenticar();
  return useMutation({
    mutationFn: (input: RegisterInput) => api.post<AuthStatusDTO>('/api/auth/register', input),
    onSuccess: aposAutenticar,
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AuthStatusDTO>('/api/auth/logout'),
    onSuccess: (status) => {
      // Limpa o cache inteiro: dado de adega não pode sobreviver ao logout.
      client.clear();
      client.setQueryData(chaves.auth, status);
    },
  });
}

/**
 * Troca a senha. A resposta traz a sessão nova deste aparelho; os outros
 * aparelhos são desconectados pelo servidor.
 */
export function useChangePassword() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api.post<AuthStatusDTO>('/api/auth/change-password', input),
    onSuccess: (status) => client.setQueryData(chaves.auth, status),
  });
}

/** Lista de adegas do usuário, para o seletor. */
export function useCellars() {
  return useQuery({
    queryKey: chaves.cellars,
    queryFn: () => api.get<CellarSummaryDTO[]>('/api/cellars'),
  });
}

/**
 * Carrega uma adega específica; sem id, a primeira do usuário.
 *
 * A ausência de adega não é erro: é o gatilho do assistente de configuração.
 * Por isso 404 vira `null` em vez de estado de falha.
 */
export function useCellar(cellarId?: number) {
  return useQuery({
    queryKey: chaves.cellar(cellarId ?? 'primeira'),
    queryFn: async () => {
      try {
        return await api.get<CellarDTO>(
          cellarId ? `/api/cellars/${cellarId}` : '/api/cellar',
        );
      } catch (erro) {
        if (
          erro instanceof ApiError &&
          (erro.code === 'CELLAR_NOT_CONFIGURED' || erro.code === 'NOT_FOUND')
        ) {
          return null;
        }
        throw erro;
      }
    },
  });
}

export function useAiStatus() {
  return useQuery({
    queryKey: chaves.aiStatus,
    queryFn: () => api.get<AiStatusDTO>('/api/ai/status'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBottles(
  params: { unplaced?: boolean; status?: string; cellarId?: number } = {},
) {
  const query = new URLSearchParams();
  if (params.unplaced) query.set('unplaced', 'true');
  if (params.status) query.set('status', params.status);
  if (params.cellarId) query.set('cellarId', String(params.cellarId));
  const qs = query.toString();

  return useQuery({
    queryKey: chaves.bottles(qs),
    queryFn: () => api.get<BottleDTO[]>(`/api/bottles${qs ? `?${qs}` : ''}`),
  });
}

export function useBottle(id: number) {
  return useQuery({
    queryKey: chaves.bottle(id),
    queryFn: () => api.get<BottleDTO>(`/api/bottles/${id}`),
    enabled: Number.isFinite(id),
  });
}

/** "Sua adega em números": contagens calculadas no servidor, sem IA. */
export function useRetrato() {
  return useQuery({
    queryKey: chaves.retrato,
    queryFn: () => api.get<RetratoDaAdegaDTO>('/api/bottles/stats'),
  });
}

export function useDrinkFirst() {
  return useQuery({
    queryKey: chaves.drinkFirst,
    queryFn: () => api.get<DrinkFirstGroup[]>('/api/bottles/drink-first'),
  });
}

/** Qualquer escrita invalida adegas + listas: grid e contadores dependem delas. */
function useInvalidarTudo() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['cellar'] });
    void client.invalidateQueries({ queryKey: chaves.cellars });
    void client.invalidateQueries({ queryKey: ['bottles'] });
    void client.invalidateQueries({ queryKey: chaves.drinkFirst });
    void client.invalidateQueries({ queryKey: chaves.retrato });
  };
}

export function useCreateCellar() {
  const invalidar = useInvalidarTudo();
  return useMutation({
    mutationFn: (input: {
      name: string;
      climatizacao?: Climatizacao | null;
      papel?: PapelDaAdega | null;
      shelves: Array<{ label?: string; capacity: number }>;
    }) => api.post<CellarDTO>('/api/cellars', input),
    onSuccess: invalidar,
  });
}

/**
 * Fila para entrar na adega, já ordenada pelo servidor: garrafas sem posição de
 * todas as adegas, mais as que não estão em adega nenhuma.
 */
/**
 * A última análise de composição, se houver. De graça: é só leitura do que
 * ficou guardado da vez em que ele pediu.
 */
export function useAnaliseDaAdega() {
  return useQuery({
    queryKey: chaves.analiseDaAdega,
    queryFn: () => api.get<AnaliseDaAdegaDTO | null>('/api/adega/analise'),
  });
}

/** Gera uma análise nova. É a chamada que custa — só sai por clique. */
export function useGerarAnaliseDaAdega() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AnaliseDaAdegaDTO>('/api/adega/analise', {}, ESPERA_DA_IA),
    onSuccess: (analise) => client.setQueryData(chaves.analiseDaAdega, analise),
  });
}

/**
 * Como o dono usa a adega. Sai do cache junto com o retrato quando muda: o
 * ritmo de consumo é o que faz a linha do tempo dizer se algo vai se perder.
 */
export function usePreferencias() {
  return useQuery({
    queryKey: chaves.preferencias,
    queryFn: () => api.get<PreferenciasDTO>('/api/preferencias'),
  });
}

export function useSalvarPreferencias() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<PreferenciasDTO>) =>
      api.patch<PreferenciasDTO>('/api/preferencias', input),
    onSuccess: (novas) => {
      client.setQueryData(chaves.preferencias, novas);
      // O retrato depende do ritmo: mudou a resposta, a linha do tempo muda.
      void client.invalidateQueries({ queryKey: chaves.retrato });
    },
  });
}

/**
 * Versão da API. Pública e sem sessão: é a mesma que o runbook confere por
 * curl depois de um deploy.
 */
export function useVersaoDaApi() {
  return useQuery({
    queryKey: ['versao-api'],
    queryFn: () =>
      api.get<{ version: string; ambiente: 'producao' | 'pre-producao' | 'desenvolvimento' }>(
        '/api/version',
      ),
    staleTime: Infinity,
  });
}

/**
 * Entrada por digital (passkey).
 *
 * A biometria nunca chega ao JavaScript. `startRegistration` e
 * `startAuthentication` pedem ao SISTEMA para fazer a cerimônia; o navegador
 * devolve só uma assinatura. Não existe API que entregasse a digital, nem para
 * esta página.
 *
 * O `window.PublicKeyCredential` não existir significa contexto inseguro ou
 * navegador antigo -- é por isso que o botão some em vez de dar erro ao ser
 * tocado. Em `http://192.168.0.40:5173` (o endereço de teste no celular) ele
 * nunca vai aparecer, e isso não é defeito: WebAuthn exige contexto seguro.
 */
export function passkeySuportado(): boolean {
  return typeof window !== 'undefined' && window.PublicKeyCredential !== undefined;
}

export function usePasskeys() {
  return useQuery({
    queryKey: ['passkeys'],
    queryFn: () => api.get<PasskeyDTO[]>('/api/auth/passkeys'),
    enabled: passkeySuportado(),
  });
}

export function useRegistrarPasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const opcoes = await api.post<Parameters<typeof startRegistration>[0]['optionsJSON']>(
        '/api/auth/passkeys/registro/inicio',
      );
      const resposta = await startRegistration({ optionsJSON: opcoes });
      return api.post<PasskeyDTO>('/api/auth/passkeys/registro/fim', resposta);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['passkeys'] }),
  });
}

export function useRemoverPasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/auth/passkeys/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['passkeys'] }),
  });
}

/** Entra com a digital. Não pede e-mail: o aparelho mostra as contas que guarda. */
export function useEntrarComPasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const opcoes = await api.post<Parameters<typeof startAuthentication>[0]['optionsJSON']>(
        '/api/auth/passkeys/login/inicio',
      );
      const resposta = await startAuthentication({ optionsJSON: opcoes });
      return api.post<AuthStatusDTO>('/api/auth/passkeys/login/fim', resposta);
    },
    onSuccess: (status) => client.setQueryData(chaves.auth, status),
  });
}

/**
 * Apaga a própria conta. Irreversível.
 *
 * `window.location.replace('/')` em vez de navegação do roteador: a sessão
 * acabou de deixar de existir no servidor, e metade do cache em memória fala de
 * uma conta que já não existe. Recarregar do zero é o único jeito honesto de a
 * tela parar de acreditar no que tinha.
 */
export function useExcluirConta() {
  return useMutation({
    mutationFn: () => api.delete('/api/auth/conta'),
    onSuccess: () => window.location.replace('/'),
  });
}

/**
 * Pede o link de redefinição.
 *
 * Não invalida cache nem mexe em sessão: o servidor responde a mesma coisa
 * exista ou não a conta, e a tela mostra a mesma mensagem nos dois casos.
 */
export function useEsqueciSenha() {
  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ mensagem: string }>('/api/auth/esqueci-senha', { email }),
  });
}

/**
 * Redefine a senha pelo token do link.
 *
 * Não loga ninguém — a pessoa volta para a tela de entrada e digita a senha
 * nova. `useInvalidarTudo` porque o servidor derruba TODAS as sessões da conta,
 * inclusive a deste aparelho se houver uma: o cache precisa parar de acreditar
 * no que tinha.
 */
export function useRedefinirSenha() {
  const invalidar = useInvalidarTudo();
  return useMutation({
    mutationFn: (dados: { token: string; novaSenha: string }) =>
      api.post<{ mensagem: string }>('/api/auth/redefinir-senha', dados),
    onSuccess: () => invalidar(),
  });
}

/**
 * A fila de quem espera vaga.
 *
 * `paraAdega` é a adega onde uma vaga acabou de abrir, e muda a ORDEM da fila —
 * não o conjunto. Numa adega de passagem, a vaga pertence a quem deve ser bebido
 * antes, e não a quem tem mais guarda pela frente. Quem decide é o servidor
 * (`domain/fila.ts`); aqui só se diz o destino.
 *
 * Entra na chave do cache: a mesma fila em duas ordens são duas respostas, e
 * compartilhar a entrada faria a tela mostrar a ordem da outra.
 */
export function useWaiting(paraAdega?: number) {
  return useQuery({
    queryKey: ['bottles', 'waiting', paraAdega ?? null],
    queryFn: () =>
      api.get<BottleDTO[]>(
        paraAdega ? `/api/bottles/waiting?paraAdega=${paraAdega}` : '/api/bottles/waiting',
      ),
  });
}

/** Nome e/ou climatização. Campo não enviado fica como está no servidor. */
export function useUpdateCellar() {
  const invalidar = useInvalidarTudo();
  return useMutation({
    mutationFn: ({
      id,
      ...dados
    }: {
      id: number;
      name?: string;
      climatizacao?: Climatizacao;
      papel?: PapelDaAdega;
    }) =>
      api.patch<CellarDTO>(`/api/cellars/${id}`, dados),
    onSuccess: invalidar,
  });
}

export function useAddShelf(cellarId: number) {
  const invalidar = useInvalidarTudo();
  return useMutation({
    mutationFn: (input: { label?: string; capacity: number }) =>
      api.post<CellarDTO>(`/api/cellars/${cellarId}/shelves`, input),
    onSuccess: invalidar,
  });
}

export function useUpdateShelf() {
  const invalidar = useInvalidarTudo();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: number;
      label?: string;
      capacity?: number;
      force?: boolean;
    }) => api.patch<CellarDTO>(`/api/shelves/${id}`, input),
    onSuccess: invalidar,
  });
}

export function useCreateBottle() {
  const invalidar = useInvalidarTudo();
  return useMutation({
    mutationFn: (input: CreateBottleInput) => api.post<BottleDTO>('/api/bottles', input),
    onSuccess: invalidar,
  });
}

export function useUpdateBottle(id: number) {
  const invalidar = useInvalidarTudo();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateBottleInput) => api.patch<BottleDTO>(`/api/bottles/${id}`, input),
    onSuccess: () => {
      invalidar();
      void client.invalidateQueries({ queryKey: chaves.bottle(id) });
    },
  });
}

/**
 * Coloca uma garrafa da fila numa vaga que acabou de abrir.
 *
 * Diferente de `useUpdateBottle`, a garrafa alvo vem na chamada e não na
 * criação do hook: quem está na tela é a garrafa que foi bebida, e a que se
 * move é outra — a primeira da fila no momento do clique.
 */
export function useOcuparVaga() {
  const invalidar = useInvalidarTudo();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      bottleId,
      ...vaga
    }: {
      bottleId: number;
      cellarId: number;
      shelfId: number;
      position: number;
    }) => api.patch<BottleDTO>(`/api/bottles/${bottleId}`, vaga),
    onSuccess: (_resposta, { bottleId }) => {
      invalidar();
      void client.invalidateQueries({ queryKey: chaves.bottle(bottleId) });
    },
  });
}

export function useConsumeBottle(id: number) {
  const invalidar = useInvalidarTudo();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { consumedAt?: string; rating?: number | null; note?: string | null }) =>
      api.post<BottleDTO>(`/api/bottles/${id}/consume`, input),
    onSuccess: () => {
      invalidar();
      void client.invalidateQueries({ queryKey: chaves.bottle(id) });
    },
  });
}

/** Desfaz o "Bebi esta": a garrafa volta para a adega, se possível à mesma vaga. */
export function useUnconsumeBottle(id: number) {
  const invalidar = useInvalidarTudo();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<BottleDTO>(`/api/bottles/${id}/consume`),
    onSuccess: (bottle) => {
      invalidar();
      client.setQueryData(chaves.bottle(id), bottle);
    },
  });
}

/**
 * Análise da adega inteira (janela + pico), em lote. Invalida as listas porque
 * a ordem de "Beber antes" e as urgências do grid mudam com o resultado.
 */
export function useAnalyzeWindows() {
  const invalidar = useInvalidarTudo();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (escopo: 'sem_informacao' | 'todas') =>
      api.post<AnalyzeWindowsResultDTO>('/api/bottles/analyze-windows', { escopo }, ESPERA_DA_IA),
    onSuccess: () => {
      invalidar();
      // O detalhe de cada garrafa também mostra janela e pico.
      void client.invalidateQueries({ queryKey: ['bottle'] });
    },
  });
}

/**
 * Cópias de uma garrafa (várias iguais). Cada destino vira uma garrafa:
 * `{ shelfId, position }` ou `null` para sem posição.
 */
export function useDuplicateBottle(id: number) {
  const invalidar = useInvalidarTudo();
  return useMutation({
    mutationFn: (destinos: Array<{ shelfId: number; position: number } | null>) =>
      api.post<BottleDTO[]>(`/api/bottles/${id}/duplicate`, { destinos }),
    onSuccess: invalidar,
  });
}

/** Exclusão de cadastro errado. Para garrafa bebida, o caminho é useConsumeBottle. */
export function useDeleteBottle(id: number) {
  const invalidar = useInvalidarTudo();
  return useMutation({
    mutationFn: () => api.delete(`/api/bottles/${id}`),
    // Só as listas: a consulta da própria garrafa não é invalidada, senão a tela
    // de detalhe (ainda montada) refaria a busca e piscaria "não encontrada"
    // antes de navegar para fora.
    onSuccess: invalidar,
  });
}

/**
 * "Analisar com IA" — sempre por clique, nunca automático. Pode ler o
 * contrarrótulo antes, e a análise vale para as garrafas iguais: por isso
 * invalida todas as garrafas, não só esta.
 */
export function useEnrichBottle(id: number) {
  const invalidar = useInvalidarTudo();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<BottleDTO>(`/api/bottles/${id}/enrich`, undefined, ESPERA_DA_IA),
    onSuccess: (bottle) => {
      client.setQueryData(chaves.bottle(id), bottle);
      invalidar();
      void client.invalidateQueries({ queryKey: ['bottle'] });
    },
  });
}

/** Passo 1 do cadastro: sobe uma foto (frente ou verso) e devolve a chave guardada. */
export function useUploadPhoto() {
  return useMutation({
    mutationFn: (arquivo: File) => api.upload<UploadedPhotoDTO>('/api/bottles/photo', arquivo, ESPERA_DO_UPLOAD),
  });
}

/**
 * Passo 2: a IA lê o rótulo a partir de fotos já guardadas. Pode ser repetido
 * sem novo upload — é o que salva o cadastro quando a página recarrega no meio.
 */
export function useReadLabel() {
  return useMutation({
    mutationFn: (fotos: { photoKey: string; backPhotoKey?: string | null }) =>
      api.post<LabelReadingDTO>('/api/bottles/read-label', fotos, ESPERA_DA_IA),
  });
}

/**
 * Destaques do que já foi bebido.
 *
 * Rota própria, como o retrato da adega: a tela pede o que vai mostrar, em vez
 * de calcular sobre a lista. Cada campo vem nulo quando o dado não sustenta a
 * afirmação, então a seção some sozinha para quem está começando.
 */
export function useDestaquesDoConsumo() {
  return useQuery({
    queryKey: chaves.destaquesDoConsumo,
    queryFn: () => api.get<DestaquesDoConsumoDTO>('/api/bottles/drunk/destaques'),
  });
}

/**
 * Quantas consultas de rótulo ainda cabem no mês.
 *
 * A tela mostra isto **antes** de gastar. Cota que só aparece no erro é cota
 * que surpreende na pior hora possível: na loja, com a garrafa na mão.
 */
export function useCotaDeConsultas(habilitado = true) {
  return useQuery({
    queryKey: chaves.cotaDeConsultas,
    queryFn: () => api.get<CotaDeConsultasDTO>('/api/bottles/consulta/cota'),
    enabled: habilitado,
  });
}

/**
 * "Que vinho é este e vale levar?" — a consulta da prateleira do mercado.
 *
 * Usa fotos já subidas por `useUploadPhoto`, como o cadastro: assim uma
 * recarga no meio (o Android descartando a aba ao abrir a câmera) não perde a
 * foto nem paga a leitura duas vezes.
 *
 * **Não cria garrafa nenhuma.** Por isso nada de invalidar listas de garrafa —
 * só a cota, que acabou de mudar.
 */
export function useConsultarRotulo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (fotos: { photoKey: string; backPhotoKey?: string | null }) =>
      api.post<ConsultaDeRotuloDTO>('/api/bottles/consultar', fotos, ESPERA_DA_IA),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaves.cotaDeConsultas }),
  });
}

export function useSuggestPairing() {
  return useMutation({
    mutationFn: (refeicao: string) =>
      api.post<PairingResponseDTO>('/api/pairing/suggest', { refeicao }, ESPERA_DA_IA),
  });
}

/**
 * Convites (área do administrador).
 *
 * As rotas respondem 404 para quem não é admin, então esta consulta só é
 * disparada quando o app já sabe que é o caso — evita um 404 no console a cada
 * abertura do app para todo mundo que não administra.
 */
export function useConvites(habilitado: boolean) {
  return useQuery({
    queryKey: chaves.convites,
    queryFn: () => api.get<ConviteDTO[]>('/api/convites'),
    enabled: habilitado,
  });
}

/** Quantas das vagas de fundador prometidas no site já foram. */
export function useVagasDeFundador(habilitado: boolean) {
  return useQuery({
    queryKey: chaves.vagasDeFundador,
    queryFn: () => api.get<VagasDeFundadorDTO>('/api/convites/vagas'),
    enabled: habilitado,
  });
}

/**
 * Quem pediu convite pelo site de apresentação.
 *
 * Vem de um KV na borda da Cloudflare, e não do banco — por isso a consulta pode
 * falhar sozinha, sem a tela de convites falhar junto. Sem configuração a API
 * devolve lista vazia, e a seção simplesmente não aparece.
 */
export function usePedidosDeConvite(habilitado: boolean) {
  return useQuery({
    queryKey: chaves.pedidosDeConvite,
    queryFn: () => api.get<PedidoDeConviteDTO[]>('/api/convites/pedidos'),
    enabled: habilitado,
    // Chamada que sai para fora da casa: tentar de novo sem parar transformaria
    // uma indisponibilidade da Cloudflare em rajada de requisições.
    retry: 1,
  });
}

export function useDescartarPedido() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (chave: string) =>
      api.delete(`/api/convites/pedidos/${encodeURIComponent(chave)}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaves.pedidosDeConvite }),
  });
}

export function useCriarConvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.post<ConviteCriadoDTO>('/api/convites', { email }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: chaves.convites });
      // O pedido some da lista quando vira convite, e quem apaga é o backend —
      // esta tela só precisa parar de mostrar o que já foi atendido.
      void client.invalidateQueries({ queryKey: chaves.pedidosDeConvite });
    },
  });
}

export function useRegenerarConvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<ConviteCriadoDTO>(`/api/convites/${id}/regenerar`),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaves.convites }),
  });
}

export function useRevogarConvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/convites/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaves.convites }),
  });
}
